import { useEffect, useState } from "react";
import { useEditorStore } from "@/store";

interface SourceLayerInfo {
  id: string;
  description?: string;
  minzoom?: number;
  maxzoom?: number;
}

// Cache TileJSON responses by URL to avoid refetching
const tileJsonCache = new Map<string, SourceLayerInfo[]>();

/**
 * Returns available source-layer names for a given source ID.
 *
 * Strategy:
 * 1. Fetch vector_layers from TileJSON endpoint (if source has a `url`)
 * 2. Fall back to extracting from layers in the style that reference this source
 */
export function useSourceLayers(sourceId: string | undefined) {
  const [remoteLayers, setRemoteLayers] = useState<SourceLayerInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const sources = useEditorStore((s) => s.style.sources);
  const layers = useEditorStore((s) => s.style.layers);

  const source = sourceId
    ? (sources as Record<string, Record<string, unknown>> | undefined)?.[sourceId]
    : undefined;

  const sourceType = source?.type as string | undefined;
  const tileJsonUrl = source?.url as string | undefined;

  // Fetch from TileJSON endpoint
  useEffect(() => {
    if (sourceType !== "vector" || !tileJsonUrl) {
      setRemoteLayers([]);
      return;
    }

    const cached = tileJsonCache.get(tileJsonUrl);
    if (cached) {
      setRemoteLayers(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(tileJsonUrl)
      .then((r) => r.json())
      .then((json: { vector_layers?: Array<{ id: string; description?: string; minzoom?: number; maxzoom?: number }> }) => {
        if (cancelled) return;
        const vl = (json.vector_layers ?? []).map((l) => ({
          id: l.id,
          description: l.description,
          minzoom: l.minzoom,
          maxzoom: l.maxzoom,
        }));
        tileJsonCache.set(tileJsonUrl, vl);
        setRemoteLayers(vl);
      })
      .catch(() => {
        if (!cancelled) setRemoteLayers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceType, tileJsonUrl]);

  // Extract from existing style layers as fallback
  const styleSourceLayers = new Set<string>();
  for (const layer of layers) {
    if (layer.source === sourceId && layer["source-layer"]) {
      styleSourceLayers.add(layer["source-layer"]);
    }
  }

  // Merge: remote layers first, then any from the style not in remote
  const remoteIds = new Set(remoteLayers.map((l) => l.id));
  const merged: SourceLayerInfo[] = [
    ...remoteLayers,
    ...[...styleSourceLayers]
      .filter((id) => !remoteIds.has(id))
      .map((id) => ({ id })),
  ];

  return { sourceLayers: merged, loading };
}
